// Centralized propType definitions
import PropTypes from "prop-types";
import ImmutablePropTypes from "react-immutable-proptypes";

import CheckboxStates from "../constants/checkboxStates";
import immutableRecords from "./immutableRecords";

const { shape, number, bool, string, element, func, oneOfType, arrayOf, object } = PropTypes;

const arrayOfStrings = arrayOf(string);
const arrayOrString = oneOfType([string, arrayOfStrings]);

// Various children selectors
const arrayOfElements = arrayOf(element);
const elementOrArray = oneOfType([element, arrayOfElements, arrayOf(oneOfType([element, arrayOfElements, string]))]);
const elementOrArrayOrList = oneOfType([elementOrArray, ImmutablePropTypes.list]);
// const elementOrFunc = oneOfType([element, func]);
// const funcOrArray = oneOfType([func, arrayOf(func)]);
// const elementOrFuncOrArray = oneOfType([elementOrFunc, arrayOf(elementOrFunc)]);
const elementOrArrayOrString = oneOfType([elementOrArray, string]);

// TODO: For some reason, validation of the passed in type is failing
const idList = _t =>
  shape({
    byId: ImmutablePropTypes.mapOf(PropTypes.any, string).isRequired,
    allIds: ImmutablePropTypes.listOf(string).isRequired
  });

// const idForTypeList = _t =>
//   shape({
//     byId: ImmutablePropTypes.mapOf(PropTypes.any, string).isRequired,
//     allIds: ImmutablePropTypes.listOf(string).isRequired,
//     allIdsForType: ImmutablePropTypes.mapOf(ImmutablePropTypes.listOf(string), string).isRequired
//   });

const checkboxState = state =>
  state === true ||
  state === false ||
  state === CheckboxStates.CHECKED ||
  state === CheckboxStates.UNCHECKED ||
  state === CheckboxStates.MIXED;

// Basic shapes
const size = shape({
  width: number.isRequired,
  height: number.isRequired
});

const range = shape({
  min: number.isRequired,
  max: number.isRequired
});

const point = shape({
  x: number.isRequired,
  y: number.isRequired
});

const line = shape({
  x1: number.isRequired,
  y1: number.isRequired,
  x2: number.isRequired,
  y2: number.isRequired
});

const flatRect = shape({
  left: number,
  right: number,
  top: number,
  bottom: number,
  width: number,
  height: number
});

// Definition Data

const propertyDefinition = shape({
  id: string.isRequired,
  key: string,
  label: string.isRequired,
  format: string.isRequired,
  allowed: ImmutablePropTypes.listOf(immutableRecords.ListOptionRecord),
  multiple: bool,
  autoCalculate: bool,
  autoCalculateType: string
});

const referenceDefinition = shape({
  id: string.isRequired,
  key: string,
  label: string.isRequired,
  types: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  icon: string,
  bidirectional: bool,
  inverseLabel: string,
  showInverse: bool
});

const denormalisedReferenceDefinition = shape({
  id: string.isRequired,
  key: string,
  label: string.isRequired,
  types: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  inverseTypes: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  icon: string,
  bidirectional: bool,
  inverseLabel: string,
  showInverse: bool
});

const typeDefinition = shape({
  id: string.isRequired,
  label: string.isRequired,
  references: ImmutablePropTypes.listOf(string).isRequired,
  properties: ImmutablePropTypes.listOf(string).isRequired,
  superTypes: ImmutablePropTypes.listOf(string).isRequired,
  icon: string.isRequired,
  color: string.isRequired,
  canHaveDate: bool.isRequired,
  canIncludeInNarrative: bool.isRequired,
  showInSequence: bool.isRequired,
  showOnSidebar: bool.isRequired,
  showAsColumn: bool.isRequired,
  identifierPrefix: string,
  compactDisplay: string.isRequired,
  category: string.isRequired
});

const listTypeDefinition = ImmutablePropTypes.listOf(typeDefinition);

const denormalisedTypeDefinition = shape({
  id: string.isRequired,
  label: string.isRequired,
  references: ImmutablePropTypes.listOf(denormalisedReferenceDefinition).isRequired,
  inverseReferences: ImmutablePropTypes.listOf(denormalisedReferenceDefinition).isRequired,
  properties: ImmutablePropTypes.listOf(propertyDefinition).isRequired,
  superTypes: ImmutablePropTypes.listOf(string).isRequired,
  icon: string.isRequired,
  color: string.isRequired,
  canHaveDate: bool.isRequired,
  canIncludeInNarrative: bool.isRequired,
  showInSequence: bool.isRequired,
  showOnSidebar: bool.isRequired,
  showAsColumn: bool.isRequired,
  identifierPrefix: string,
  compactDisplay: string.isRequired,
  category: string.isRequired
});

const colorDefinition = shape({
  id: string.isRequired,
  label: string.isRequired,
  swatchColor: string.isRequired
});

const propertyDefinitionsIdList = idList(propertyDefinition);
const denormalisedReferenceDefinitionsIdList = idList(denormalisedReferenceDefinition);
const denormalisedTypeDefinitionsIdList = idList(denormalisedTypeDefinition);
const colorDefinitionsIdList = idList(colorDefinition);

const timelineViewSettings = shape({
  scrollBounds: string.isRequired,
  eventLayout: string.isRequired,
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showRelationshipTypes: ImmutablePropTypes.listOf(string).isRequired,
  showImage: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired,
  zoomFrom: number.isRequired,
  zoomTo: number.isRequired,
  showTypes: ImmutablePropTypes.listOf(string).isRequired
});

const timelineViewSettingsJs = shape({
  scrollBounds: string.isRequired,
  eventLayout: string.isRequired,
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showRelationshipTypes: ImmutablePropTypes.listOf(string).isRequired,
  showImage: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired,
  zoomFrom: number.isRequired,
  zoomTo: number.isRequired,
  showTypes: arrayOf(string).isRequired
});

const narrativeViewSettings = shape({
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showRelationshipTypes: PropTypes.oneOfType([ImmutablePropTypes.listOf(string), arrayOfStrings]).isRequired,
  showImage: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired
});

const narrativeSettings = shape({
  labels: ImmutablePropTypes.list.isRequired,
  autoDeleteWhenRemoveContainer: PropTypes.bool.isRequired
});

const itemCardViewSettings = shape({
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showRelationshipTypes: ImmutablePropTypes.listOf(string).isRequired,
  showImage: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired
});

const relationshipViewSettings = shape({
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired
});

const mindmapViewSettings = shape({
  showIdentifier: bool.isRequired,
  showDates: bool.isRequired,
  showDuration: bool.isRequired,
  showNarrative: bool.isRequired,
  showRelationshipTypes: ImmutablePropTypes.listOf(string).isRequired,
  showImage: bool.isRequired,
  showSummary: bool.isRequired,
  showInfoIcons: bool.isRequired
});

const subwayViewSettings = shape({
  showDates: bool,
  display: string
});

const timelineDefinitions = shape({
  types: denormalisedTypeDefinitionsIdList.isRequired,
  properties: propertyDefinitionsIdList.isRequired,
  references: denormalisedReferenceDefinitionsIdList.isRequired,
  colors: colorDefinitionsIdList.isRequired,
  timelineViewSettings: timelineViewSettings.isRequired,
  narrativeViewSettings: narrativeViewSettings.isRequired,
  narrativeSettings: narrativeSettings.isRequired,
  relationshipViewSettings: relationshipViewSettings.isRequired,
  mobileItemCardViewSettings: itemCardViewSettings.isRequired,
  mindmapViewSettings: mindmapViewSettings.isRequired,
  subwayViewSettings: subwayViewSettings.isRequired
});

// Filter data
const listFilterPart = shape({
  terms: ImmutablePropTypes.listOf(PropTypes.string),
  match: PropTypes.string
});

const rangeFilterPart = shape({
  above: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.instanceOf(immutableRecords.DateRecord),
    PropTypes.instanceOf(immutableRecords.TimespanRecord),
    ImmutablePropTypes.list
  ]),
  below: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.instanceOf(immutableRecords.DateRecord),
    PropTypes.instanceOf(immutableRecords.TimespanRecord),
    ImmutablePropTypes.list
  ]),
  isAboveIncluded: PropTypes.bool,
  isBelowIncluded: PropTypes.bool
});

const boolFilterPart = shape({
  expected: PropTypes.bool
});

const filter = shape({
  matchCriteria: PropTypes.string,
  label: listFilterPart,
  summary: listFilterPart,
  tags: listFilterPart,
  type: listFilterPart,
  color: listFilterPart,
  super: listFilterPart,
  date: rangeFilterPart,
  duration: rangeFilterPart,
  narrative: rangeFilterPart,
  relationshipsByType: ImmutablePropTypes.map,
  relationshipsByReference: ImmutablePropTypes.map,
  textProperties: ImmutablePropTypes.map,
  numericProperties: ImmutablePropTypes.map,
  boolProperties: ImmutablePropTypes.map
});

// Model data
const textMetrics = shape({
  lines: oneOfType([ImmutablePropTypes.listOf(string), arrayOf(string)]).isRequired,
  size: size.isRequired
});

const textSize = shape({
  text: string,
  size: size.isRequired
});

const entityItem = shape({
  id: string.isRequired,
  label: string,
  color: string.isRequired
});

const entityItemList = ImmutablePropTypes.listOf(entityItem);

const itemType = shape({
  id: string.isRequired,
  icon: string.isRequired,
  color: string.isRequired,
  label: string.isRequired
});

const itemTypeList = ImmutablePropTypes.listOf(itemType);

const mindmapItem = shape({
  id: string.isRequired,
  label: string.isRequired,
  selected: bool
});

const mindmapList = ImmutablePropTypes.listOf(mindmapItem);

// Calendar Data

const era = shape({
  name: string.isRequired,
  shortName: string.isRequired,
  isBackwards: bool.isRequired,
  hasLeapYears: bool.isRequired,
  duration: number.isRequired
});

const month = shape({
  name: string.isRequired,
  shortName: string.isRequired,
  normalDuration: number.isRequired,
  leapDuration: number.isRequired
});

const weekday = shape({
  name: string.isRequired,
  shortName: string.isRequired
});

const calendar = shape({
  eras: ImmutablePropTypes.listOf(era).isRequired,
  months: ImmutablePropTypes.listOf(month).isRequired,
  weekdays: ImmutablePropTypes.listOf(weekday).isRequired,
  hoursInDay: number.isRequired,
  zeroDateTimestamp: number.isRequired,
  weekdayIndexAtZero: number.isRequired
});

// Generic Object Data

const timespan = shape({
  years: number.isRequired,
  months: number.isRequired,
  weeks: number.isRequired,
  days: number.isRequired,
  hours: number.isRequired,
  minutes: number.isRequired,
  seconds: number.isRequired
});

const timespanDisplay = shape({
  years: number,
  months: number,
  weeks: number,
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
  label: string
});

const timespanRecord = PropTypes.instanceOf(immutableRecords.TimespanRecord);
const timespanDisplayRecord = PropTypes.instanceOf(immutableRecords.TimespanDisplayRecord);

const date = shape({
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timestamp: number,
  precision: number
});

const dateDisplay = shape({
  era: number,
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timestamp: number,
  weekday: number,
  precision: number,
  label: string
});

const dateRecord = PropTypes.instanceOf(immutableRecords.DateRecord);
const dateDisplayRecord = PropTypes.instanceOf(immutableRecords.DateDisplayRecord);

const dateRelated = PropTypes.oneOfType([date, dateDisplay, dateRecord, dateDisplayRecord]);

const avatarDisplay = shape({
  image: string,
  initials: string,
  color: string
});

const avatarDisplayList = ImmutablePropTypes.listOf(avatarDisplay);
const avatarDisplayArray = PropTypes.arrayOf(avatarDisplay);

// const item = PropTypes.oneOf([
//   PropTypes.instanceOf(immutableRecords.ItemRecord),
//   PropTypes.instanceOf(immutableRecords.ItemDisplayRecord)
// ]);

const item = PropTypes.instanceOf(immutableRecords.ItemRecord);

// shape({
//   // Normal properties of Event
//   id: string.isRequired,
//   type: string.isRequired,
//   displayId: string.isRequired,
//   label: string,
//   summary: string,
//   color: string.isRequired,
//   super: string,
//   initials: string,
//   ongoing: bool.isRequired,
//   startDate: date,
//   duration: timespan,
//   showAnniversary: bool,
//   anniversaryIcon: string,
//   references: ImmutablePropTypes.listOf(string),
//   inverseReferences: ImmutablePropTypes.listOf(string),
//   blockingDependencies: validatedDependencyList,
//   blockedByDependencies: validatedDependencyList,
//   dateConstraints: validatedConstraint,
//   propertyValues: ImmutablePropTypes.mapOf(propertyDefinition),
//   tags: ImmutablePropTypes.listOf(string)
// });

const itemList = ImmutablePropTypes.listOf(item);

const displayItem = PropTypes.instanceOf(immutableRecords.ItemDisplayRecord);

// const displayItem = shape({
//   // Normal properties of Event
//   id: string.isRequired,
//   type: string.isRequired,
//   displayId: string.isRequired,
//   label: string,
//   color: string.isRequired,
//   ongoing: bool.isRequired,
//   startDate: date,
//   duration: timespan,
//   super: string,
//   superLabel: string,
//   superColor: string,
//   children: ImmutablePropTypes.listOf(string),
//   narrativePosition: ImmutablePropTypes.listOf(number),
//   sequenceIndex: number,
//   titleMetrics: textMetrics.isRequired,
//   subwayTitleMetrics: textMetrics.isRequired,
//   summaryMetrics: textMetrics.isRequired,
//   dateTextSize: textSize.isRequired,
//   durationTextSize: textSize.isRequired,
//   narrativeTextSize: textSize.isRequired,
//   identifierSize: size.isRequired,
//   itemCardSize: size.isRequired,
//   relatedItemsByType: ImmutablePropTypes.list,
//   index: number.isRequired,
//   selected: bool.isRequired
// });

const displayItemList = ImmutablePropTypes.listOf(displayItem);
const displayItemMap = ImmutablePropTypes.mapOf(PropTypes.any, PropTypes.string);

const permissibleDisplayItem = displayItem;

// const permissibleDisplayItem = shape({
//   // Normal properties of Event
//   id: string.isRequired,
//   type: string,
//   displayId: string,
//   label: string,
//   color: string,
//   ongoing: bool,
//   startDate: PropTypes.any,
//   duration: timespan,
//   super: string,
//   superLabel: string,
//   superColor: string,
//   titleMetrics: textMetrics,
//   summaryMetrics: textMetrics,
//   dateTextSize: textSize,
//   durationTextSize: textSize,
//   narrativeTextSize: textSize,
//   identifierSize: size,
//   itemCardSize: size,
//   relatedItemsByType: ImmutablePropTypes.list,
//   index: number,
//   selected: bool
// });

// const permissibleDisplayItemList = ImmutablePropTypes.listOf(permissibleDisplayItem);

// Mindmap View Data

const mindmapNodeEntityValues = shape({
  label: string.isRequired,
  color: string.isRequired,
  initials: string.isRequired,
  type: itemType.isRequired
});

const mindmapEventNode = shape({
  id: string.isRequired,
  itemId: string.isRequired,
  rect: flatRect.isRequired,
  nodeType: string.isRequired,
  itemDisplay: permissibleDisplayItem.isRequired,
  typeData: itemType.isRequired
});

const mindmapEntityNode = shape({
  id: string.isRequired,
  itemId: string.isRequired,
  left: number.isRequired,
  top: number.isRequired,
  right: number.isRequired,
  bottom: number.isRequired,
  nodeType: string.isRequired,
  index: number.isRequired,
  color: string.isRquired,
  selected: bool.isRequired,
  entityValues: mindmapNodeEntityValues
});

const mindmapConnector = shape({
  id: number.isRequired,
  color: string.isRequired,
  points: ImmutablePropTypes.listOf(point).isRequired
  // start: point.isRequired,
  // end: point.isRequired
});

const mindmap = shape({
  // nodes: ImmutablePropTypes.listOf(oneOfType([mindmapEventNode, mindmapEntityNode])).isRequired,
  nodes: ImmutablePropTypes.list.isRequired,
  connectors: ImmutablePropTypes.listOf(mindmapConnector).isRequired,
  rect: flatRect.isRequired
});

// const subwayNode = PropTypes.instanceOf(immutableRecords.SubwayNodeDisplayRecord);
const subwayNodeList = ImmutablePropTypes.listOf(immutableRecords.SubwayNodeDisplayRecord);
// const subwayThread = PropTypes.instanceOf(immutableRecords.SubwayThreadDisplayRecord);
const subwayData = PropTypes.instanceOf(immutableRecords.SubwayDisplayRecord);

const narrativeCard = shape({
  itemDisplay: permissibleDisplayItem.isRequired,
  rect: flatRect.isRequired,
  position: ImmutablePropTypes.listOf(number).isRequired,
  cards: ImmutablePropTypes.listOf(PropTypes.any).isRequired
});

const narrativeGroup = shape({
  label: string.isRequired,
  id: string.isRequired,
  cards: ImmutablePropTypes.listOf(narrativeCard).isRequired,
  rect: flatRect.isRequired,
  position: ImmutablePropTypes.listOf(number).isRequired
});

const narrativeColumn = shape({
  label: string.isRequired,
  id: string.isRequired,
  groups: ImmutablePropTypes.listOf(narrativeGroup).isRequired,
  rect: flatRect.isRequired,
  position: ImmutablePropTypes.listOf(number).isRequired
});

const narrativeColumnList = ImmutablePropTypes.listOf(narrativeColumn);
const narrativeCardList = ImmutablePropTypes.listOf(narrativeCard);

const narrativeData = shape({
  cards: narrativeCardList.isRequired,
  size: size.isRequired
});

const narrativePosition = arrayOf(number);

const itemCardRow = PropTypes.instanceOf(immutableRecords.ItemCardDisplayRecord);
const itemCardListData = PropTypes.instanceOf(immutableRecords.ItemCardListDisplayRecord);

const relationshipIntersection = PropTypes.instanceOf(immutableRecords.RelationshipEventColumnDataDisplayRecord);
const relationshipIntersectionList = ImmutablePropTypes.listOf(relationshipIntersection);
const relationshipIntersectionMap = ImmutablePropTypes.mapOf(PropTypes.any, string);

const relationshipRow = PropTypes.instanceOf(immutableRecords.RelationshipItemDisplayRecord);
const relationshipColumn = PropTypes.instanceOf(immutableRecords.RelationshipColumnDisplayRecord);
const relationshipColumnList = ImmutablePropTypes.listOf(relationshipColumn);
const relationshipColumnGroup = PropTypes.instanceOf(immutableRecords.RelationshipColumnGroupDisplayRecord);
const relationshipColumnGroupList = ImmutablePropTypes.listOf(immutableRecords.RelationshipColumnGroupDisplayRecord);
const relationshipData = PropTypes.instanceOf(immutableRecords.RelationshipViewDisplayRecord);

const identifier = PropTypes.instanceOf(immutableRecords.IdentifierDisplayRecord);
const identifierList = ImmutablePropTypes.listOf(identifier);

const tableCell = PropTypes.instanceOf(immutableRecords.TableCellRecord);
const tableCellList = ImmutablePropTypes.listOf(tableCell);
const tableRow = PropTypes.instanceOf(immutableRecords.TableRowRecord);
const tableColumn = PropTypes.instanceOf(immutableRecords.TableColumnRecord);
const tableColumnList = ImmutablePropTypes.listOf(tableColumn);
const availableTableColumn = PropTypes.instanceOf(immutableRecords.TableAvailableColumnRecord);
const availableTableColumnList = ImmutablePropTypes.listOf(availableTableColumn);
const tableData = PropTypes.instanceOf(immutableRecords.TableRecord);
const tableCellPosition = PropTypes.instanceOf(immutableRecords.TableCellPositionRecord);
const tableCellPositionList = ImmutablePropTypes.listOf(tableCellPosition);

const allowedReferencePerSubjectObjectMap = ImmutablePropTypes.mapOf(PropTypes.any, string);

// Specific Views
const splitConstraints = shape({
  hasDivider: bool.isRequired,
  canResize: bool.isRequired,
  canCollapse: bool.isRequired,
  minSize: number.isRequired,
  maxSize: number.isRequired,
  preferredSize: number.isRequired,
  defaultSize: number.isRequired
});
const splitConstraintsArray = arrayOf(splitConstraints);
const splitConstraintsList = ImmutablePropTypes.listOf(splitConstraints);

const popoverListOption = shape({
  id: string.isRequired,
  label: string.isRequired,
  selected: bool,
  disabled: bool
});
const popoverListGroup = arrayOf(popoverListOption);
const popoverListGroupArray = arrayOf(popoverListGroup);

// Timeline View
const calendarMarker = shape({
  // Normal properties of Event
  id: string.isRequired,
  label: string,
  color: string.isRequired,
  icon: string,
  drawOnTimeline: bool.isRequired,
  startDate: date,
  duration: timespan,
  repeat: bool.isRequired,
  repeatUntil: date,
  repeatFrequency: timespan
});

const calendarMarkerList = ImmutablePropTypes.listOf(calendarMarker);

// Timeline View
const displayCalendarMarker = shape({
  // Normal properties of Event
  id: string.isRequired,
  label: string.isRequired,
  color: string.isRequired,
  icon: string,
  drawOnTimeline: bool.isRequired,
  startDate: date.isRequired,
  endDate: date.isRequired
});

const displayCalendarMarkerList = ImmutablePropTypes.listOf(displayCalendarMarker);

const displayAnniversary = shape({
  // Normal properties of Event
  id: string.isRequired,
  itemLabel: string.isRequired,
  type: string.isRequired,
  color: string.isRequired,
  age: number.isRequired,
  date: date.isRequired
});

const displayAnniversaryList = ImmutablePropTypes.listOf(displayAnniversary);

// Workspace Arrangement
const workView = shape({
  viewId: string.isRequired,
  viewType: string.isRequired,
  expandedItems: ImmutablePropTypes.listOf(string),
  mindmapId: string,
  label: string.isRequired,
  isActive: bool.isRequired,
  isFilterActive: bool.isRequired,
  size: size.isRequired,
  storedSize: size.isRequired,
  minSize: size.isRequired
});

let viewLayout = shape({
  orientation: string.isRequired,
  viewType: string.isRequired,
  childViewTypes: string.isRequired,
  size: size.isRequired,
  minSize: size.isRequired,
  scrollSync: bool.isRequired
});

viewLayout.children = ImmutablePropTypes.listOf(oneOfType([workView, viewLayout]));

// Item Card Data

const timelineViewEventPosition = shape({
  markerRelativeRect: flatRect,
  eventCardRelativeRect: flatRect,
  totalRect: flatRect
});

// Inspector
const inspectorTab = shape({
  icon: string,
  label: string.isRequired,
  itemLabel: string,
  itemColor: string,
  showLabel: bool,
  badge: number,
  badgeColor: string,
  view: element.isRequired,
  preview: element
});

const inspectorTabArray = arrayOf(inspectorTab);

const inspectorRelationship = shape({
  id: string.isRequired,
  subject: string.isRequired,
  reference: string.isRequired,
  object: string.isRequired,
  objectLabel: string.isRequired,
  objectIsAlive: bool.isRequired,
  objectAge: string.isRequired
});

const inspectorDependency = PropTypes.instanceOf(immutableRecords.InspectorDependencyRecord);
const inspectorConstraint = PropTypes.instanceOf(immutableRecords.InspectorConstraintRecord);

const inspectorDependenciesList = ImmutablePropTypes.listOf(immutableRecords.InspectorDependencyRecord);
const inspectorConstraintsList = ImmutablePropTypes.listOf(immutableRecords.InspectorConstraintRecord);

const inspectorItem = shape({
  id: string,
  type: string,
  displayId: string,
  label: string,
  color: string,
  ongoing: bool,
  startDate: dateDisplay,
  endDate: dateDisplay,
  duration: timespanDisplay,
  relationshipsByRefType: ImmutablePropTypes.map,
  inverseRelationshipsByRefType: ImmutablePropTypes.map,
  blockingDependencies: inspectorDependenciesList,
  blockedByDependencies: inspectorDependenciesList,
  dateConstraints: inspectorConstraintsList,
  showAnniversary: bool,
  anniversaryIcon: string,
  tags: ImmutablePropTypes.listOf(PropTypes.string),
  hasChildren: bool,
  hasDates: bool
});

const inspectorItemList = ImmutablePropTypes.listOf(inspectorItem);

const contextEventDotRecord = shape({
  id: string,
  color: string,
  startTs: number,
  endTs: number
});

// Search results
const searchResult = shape({
  id: string.isRequired,
  typeData: itemType.isRequired,
  displayId: string.isRequired,
  initials: string,
  label: string.isRequired,
  summary: string,
  color: string.isRequired,
  identifierSize: size.isRequired,
  selected: bool.isRequired
});

const searchResultList = ImmutablePropTypes.listOf(searchResult);

// Tree View
const treeViewItem = shape({
  id: string.isRequired,
  label: string,
  placeholder: string,
  icon: string,
  statusIcon: string,
  labelColor: string,
  iconColor: string,
  statusColor: string,
  children: ImmutablePropTypes.listOf(PropTypes.any)
});

const treeViewItemList = ImmutablePropTypes.listOf(treeViewItem);

// Notification Data
const uiNotificationAction = PropTypes.instanceOf(immutableRecords.UINotificationActionRecord);
const uiNotificationActionList = ImmutablePropTypes.listOf(immutableRecords.UINotificationActionRecord);
const uiNotification = PropTypes.instanceOf(immutableRecords.UINotificationRecord);

// Component Props
const listOption = shape({
  label: string,
  value: oneOfType([string, number]),
  color: string,
  icon: string,
  selected: bool,
  description: string,
  className: string,
  canDelete: bool
});

const listOptionGroup = shape({
  label: string.isRequired,
  value: string,
  color: string,
  icon: string,
  children: ImmutablePropTypes.listOf(PropTypes.any),
  state: checkboxState
});

const listOptionArray = arrayOf(listOption);
const listOptionList = ImmutablePropTypes.listOf(listOption);
const listOptionArrayOrList = oneOfType([listOptionArray, listOptionList]);

const listOptionGroupArray = arrayOf(listOptionGroup);
const listOptionGroupList = ImmutablePropTypes.listOf(listOptionGroup);

const listOptionListByIdMap = ImmutablePropTypes.mapOf(PropTypes.any, string);

const iconOption = shape({
  color: string,
  icon: string.isRequired
});
const iconOptionArray = arrayOf(iconOption);

const formListValue = shape({
  label: string,
  subLabel: string,
  leftIcon: string,
  leftIconColor: string,
  value: string
});

const formListValueArray = arrayOf(formListValue);

const formButtonValue = shape({
  label: string.isRequired,
  onClick: func.isRequired,
  styles: object
});

const attachmentListValue = shape({
  id: string.isRequired,
  label: string,
  url: string
});

const attachmentListValueArray = ImmutablePropTypes.listOf(attachmentListValue);

const formButtonValuesArray = arrayOf(formButtonValue);

// Context HOCs

const dragAndDropRegistration = PropTypes.shape({
  registerDropTarget: PropTypes.func.isRequired,
  unregisterDropTarget: PropTypes.func.isRequired,
  registerDragSource: PropTypes.func.isRequired,
  unregisterDragSource: PropTypes.func.isRequired
});

// Welcome WelcomeTemplateRecord
const fileRecord = PropTypes.instanceOf(immutableRecords.FileRecord);
const fileRecordList = ImmutablePropTypes.listOf(fileRecord);
const welcomeTemplateRecord = PropTypes.instanceOf(immutableRecords.WelcomeTemplateRecord);
const welcomeTemplateRecordList = ImmutablePropTypes.listOf(welcomeTemplateRecord);

const validatedDependency = PropTypes.instanceOf(immutableRecords.ValidatedDependencyRecord);
const validatedDependencyList = ImmutablePropTypes.listOf(validatedDependency);
// Seems like the first parameter of .mapOf relys on
// a shape of object.
// Cannot use as an instance of Record
const validatedDependencyMap = ImmutablePropTypes.mapOf(PropTypes.any, PropTypes.string);

const validatedConstraint = PropTypes.instanceOf(immutableRecords.ValidatedConstraintRecord);
const validatedConstraintList = ImmutablePropTypes.listOf(validatedConstraint);

const scrollPosition = PropTypes.shape({
  viewport: range,
  canvas: range,
  zoom: PropTypes.number,
  absoluteScroll: PropTypes.number,
  relativeScroll: PropTypes.number
});

const scrollActions = PropTypes.shape({
  animateToViewport: PropTypes.func.isRequired,
  animateRectIntoView: PropTypes.func.isRequired,
  jumpRectIntoView: PropTypes.func.isRequired,
  jumpToViewport: PropTypes.func.isRequired,
  registerForLiveUpdates: PropTypes.func.isRequired,
  unregisterForLiveUpdates: PropTypes.func.isRequired
});

export default {
  // Various children selectors
  arrayOfElements,
  elementOrArray,
  elementOrArrayOrList,
  elementOrArrayOrString,
  arrayOfStrings,
  arrayOrString,
  // elementOrFunc,
  // elementOrFuncOrArray,
  // funcOrArray,
  // Basic shapes
  size,
  range,
  point,
  line,
  flatRect,
  // Definition Data
  denormalisedTypeDefinition,
  propertyDefinition,
  referenceDefinition,
  denormalisedReferenceDefinition,
  colorDefinition,
  typeDefinition,
  listTypeDefinition,
  denormalisedTypeDefinitionsIdList,
  propertyDefinitionsIdList,
  denormalisedReferenceDefinitionsIdList,
  colorDefinitionsIdList,
  timelineDefinitions,
  timelineViewSettings,
  timelineViewSettingsJs,
  narrativeViewSettings,
  narrativeSettings,
  relationshipViewSettings,
  itemCardViewSettings,
  mindmapViewSettings,
  subwayViewSettings,

  // Model data
  textMetrics,
  textSize,
  entityItem,
  entityItemList,
  itemType,
  itemTypeList,
  mindmapList,
  // Calendar data
  calendar,
  era,
  month,
  weekday,
  // Timespan data
  timespan,
  timespanDisplay,
  timespanRecord,
  timespanDisplayRecord,
  // Date data
  date,
  dateDisplay,
  dateRecord,
  dateDisplayRecord,
  dateRelated,
  // Mindmap View
  mindmap,
  mindmapConnector,
  mindmapEventNode,
  mindmapEntityNode,
  mindmapNodeEntityValues,
  // Narrative View
  narrativeData,
  narrativeColumnList,
  narrativeColumn,
  narrativeGroup,
  narrativeCardList,
  narrativeCard,
  narrativePosition,
  // ItemCard View
  itemCardRow,
  itemCardListData,
  // Relationship View
  relationshipData,
  relationshipColumn,
  relationshipColumnList,
  relationshipColumnGroup,
  relationshipColumnGroupList,
  relationshipRow,
  relationshipIntersection,
  relationshipIntersectionList,
  // relationshipIntersectionArray,
  relationshipIntersectionMap,
  allowedReferencePerSubjectObjectMap,
  // Timeline View
  item,
  itemList,
  displayItemList,
  displayItemMap,
  displayItem,
  calendarMarker,
  calendarMarkerList,
  displayCalendarMarker,
  displayCalendarMarkerList,
  displayAnniversary,
  displayAnniversaryList,

  // Subway View
  subwayNodeList,
  subwayData,

  // Identifier
  identifier,
  identifierList,

  // Table View
  tableCell,
  tableCellList,
  tableRow,
  tableColumn,
  tableColumnList,
  availableTableColumn,
  availableTableColumnList,
  tableData,
  tableCellPosition,
  tableCellPositionList,

  // Item Card
  avatarDisplay,
  avatarDisplayList,
  avatarDisplayArray,
  timelineViewEventPosition,

  // Context Bar
  contextEventDotRecord,

  // Specific views
  splitConstraints,
  splitConstraintsArray,
  splitConstraintsList,
  popoverListOption,
  popoverListGroup,
  popoverListGroupArray,

  // View layout
  workView,
  viewLayout,

  // Inspector Props
  inspectorTab,
  inspectorTabArray,
  inspectorRelationship,
  inspectorDependency,
  inspectorDependenciesList,
  inspectorConstraint,
  inspectorConstraintsList,
  inspectorItem,
  inspectorItemList,

  // Filter props
  listFilterPart,
  rangeFilterPart,
  boolFilterPart,
  filter,

  // Search props
  searchResult,
  searchResultList,

  // Tree View
  treeViewItem,
  treeViewItemList,

  // UINotification Data
  uiNotificationAction,
  uiNotificationActionList,
  uiNotification,

  // Component Props
  listOption,
  listOptionArray,
  listOptionList,
  listOptionArrayOrList,
  listOptionListByIdMap,
  listOptionGroup,
  listOptionGroupArray,
  listOptionGroupList,
  iconOption,
  iconOptionArray,
  formListValue,
  formListValueArray,
  formButtonValue,
  formButtonValuesArray,
  checkboxState,
  attachmentListValueArray,

  // Context HOCs
  dragAndDropRegistration,

  // Welcome
  fileRecord,
  fileRecordList,
  welcomeTemplateRecord,
  welcomeTemplateRecordList,

  // Dependency
  validatedDependency,
  validatedDependencyList,
  validatedDependencyMap,

  // Constraint
  validatedConstraint,
  validatedConstraintList,

  // ScrollableView
  scrollPosition,
  scrollActions
};

// import { PropTypeShapes } from "../../../types";
